import { graphql, readInlineData } from 'react-relay';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { useIsEligibleForStorageHardLimitExp } from '../use-is-eligible-for-storage-hard-limit-exp/index.tsx';
import type { useShouldBlockStorageUploads$key } from './__generated__/useShouldBlockStorageUploads.graphql';
/* eslint-disable @atlassian/relay/graphql-naming */

type AttachmentStorageData = {
	attachmentStorageAllowed?: number | null;
	attachmentStorageUsed?: number | null;
	attachmentStorageIsUnlimited?: boolean | null;
} & { [K in ' $fragmentSpreads']?: never };

export type ShouldBlockStorageUploadsFragmentReference = useShouldBlockStorageUploads$key | null;

export type ShouldBlockStorageUploadsPropValue =
	| ShouldBlockStorageUploadsFragmentReference
	| AttachmentStorageData
	| null;

const isPlainValue = (val?: object): val is AttachmentStorageData =>
	Boolean(val && 'attachmentStorageAllowed' in val);

const useShouldBlockStorageUploadsImpl = (
	attachmentData?: ShouldBlockStorageUploadsPropValue,
	isProjectAgnostic?: boolean,
): boolean => {
	const eligible = useIsEligibleForStorageHardLimitExp(isProjectAgnostic);

	if (!eligible || !attachmentData) {
		return false;
	}

	let data;
	if (isPlainValue(attachmentData)) {
		data = attachmentData;
	} else {
		data = readInlineData<useShouldBlockStorageUploads$key>(
			graphql`
				fragment useShouldBlockStorageUploads on JiraQuery
				@inline
				@argumentDefinitions(
					cloudId: { type: "ID!" }
					applicationKey: { type: "JiraApplicationKey!" }
				) {
					attachmentStorageAllowed(cloudId: $cloudId, applicationKey: $applicationKey)
					attachmentStorageUsed(cloudId: $cloudId, applicationKey: $applicationKey)
					attachmentStorageIsUnlimited(cloudId: $cloudId, applicationKey: $applicationKey)
				}
			`,
			attachmentData,
		);
	}

	if (!data) {
		return false;
	}

	const { attachmentStorageAllowed, attachmentStorageIsUnlimited, attachmentStorageUsed } = data;

	if (
		attachmentStorageIsUnlimited === true ||
		!attachmentStorageUsed ||
		!attachmentStorageAllowed
	) {
		return false;
	}
	if (
		attachmentStorageUsed >= attachmentStorageAllowed &&
		expValEquals('jira-storage-hard-limit', 'cohort', 'variation')
	) {
		return true;
	}
	return false;
};

export const useShouldBlockStorageUploads = functionWithCondition(
	() => fg('jira-storage-hard-limit-gate'),
	useShouldBlockStorageUploadsImpl,
	() => false,
);
